import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, Tag, AlertCircle, ToggleLeft, ToggleRight, ShoppingCart, Search, X } from 'lucide-react';

interface Discount {
  id: number;
  name: string;
  code: string | null;
  type: 'PERCENTAGE' | 'FIXED';
  value: string;
  maxAmount: string | null;
  description: string | null;
  isActive: boolean;
  validFrom: string | null;
  validTo: string | null;
  isExpired?: boolean;
  isNotYetValid?: boolean;
}

interface Product {
  id: number;
  sku: string;
  name: string;
  defaultDiscountId: number | null;
  isActive: boolean;
}

const EMPTY_FORM = {
  name: '',
  code: '',
  type: 'PERCENTAGE' as 'PERCENTAGE' | 'FIXED',
  value: '',
  maxAmount: '',
  description: '',
  isActive: true,
  validFrom: '',
  validTo: '',
};

export default function Descuentos() {
  const navigate = useNavigate();
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Discount | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');

  // Product linking state
  const [linkedProductIds, setLinkedProductIds] = useState<number[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const productDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchDiscounts();
    fetchProducts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(e.target as Node)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchDiscounts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/discounts', { credentials: 'include' });
      if (!res.ok) throw new Error('Error al cargar descuentos');
      const data = await res.json();
      setDiscounts(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch('/api/sales/products', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  };

  const getLinkedProducts = (discountId: number) =>
    products.filter(p => p.defaultDiscountId === discountId);

  const openCreate = () => {
    setEditing(null);
    setFormData({ ...EMPTY_FORM });
    setLinkedProductIds([]);
    setProductSearch('');
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (d: Discount) => {
    setEditing(d);
    setFormData({
      name: d.name,
      code: d.code || '',
      type: d.type,
      value: parseFloat(d.value).toString(),
      maxAmount: d.maxAmount ? parseFloat(d.maxAmount).toString() : '',
      description: d.description || '',
      isActive: d.isActive,
      validFrom: d.validFrom || '',
      validTo: d.validTo || '',
    });
    setLinkedProductIds(products.filter(p => p.defaultDiscountId === d.id).map(p => p.id));
    setProductSearch('');
    setFormError('');
    setShowModal(true);
  };

  const toggleProduct = (productId: number) => {
    setLinkedProductIds(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
    );
  };

  const removeLinkedProduct = (productId: number) => {
    setLinkedProductIds(prev => prev.filter(id => id !== productId));
  };

  const handleSave = async () => {
    setFormError('');
    if (!formData.name.trim()) { setFormError('El nombre es requerido'); return; }
    if (!formData.value || isNaN(parseFloat(formData.value)) || parseFloat(formData.value) <= 0) {
      setFormError('El valor debe ser un número positivo'); return;
    }
    if (formData.type === 'PERCENTAGE' && parseFloat(formData.value) > 100) {
      setFormError('El porcentaje no puede ser mayor a 100'); return;
    }
    setSaving(true);
    try {
      const body = {
        name: formData.name.trim(),
        code: formData.code.trim() || null,
        type: formData.type,
        value: parseFloat(formData.value),
        maxAmount: formData.maxAmount && !isNaN(parseFloat(formData.maxAmount)) ? parseFloat(formData.maxAmount) : null,
        description: formData.description.trim() || null,
        isActive: formData.isActive,
        validFrom: formData.validFrom || null,
        validTo: formData.validTo || null,
      };
      const url = editing ? `/api/discounts/${editing.id}` : '/api/discounts';
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al guardar');
      }
      const savedDiscount: Discount = await res.json();
      const discountId = savedDiscount.id;

      // Determine products to link and unlink
      const previouslyLinked = products
        .filter(p => p.defaultDiscountId === discountId)
        .map(p => p.id);

      const toLink = linkedProductIds.filter(id => !previouslyLinked.includes(id));
      const toUnlink = previouslyLinked.filter(id => !linkedProductIds.includes(id));

      // Update products in parallel
      await Promise.all([
        ...toLink.map(pid =>
          fetch(`/api/sales/products/${pid}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ defaultDiscountId: discountId }),
          })
        ),
        ...toUnlink.map(pid =>
          fetch(`/api/sales/products/${pid}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ defaultDiscountId: null }),
          })
        ),
      ]);

      setShowModal(false);
      await Promise.all([fetchDiscounts(), fetchProducts()]);
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar este descuento? Esta acción no se puede deshacer.')) return;
    try {
      const res = await fetch(`/api/discounts/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Error al eliminar');
      fetchDiscounts();
      fetchProducts();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleToggleActive = async (d: Discount) => {
    try {
      await fetch(`/api/discounts/${d.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: !d.isActive }),
      });
      fetchDiscounts();
    } catch { /* ignore */ }
  };

  const filtered = discounts.filter(d => {
    const matchSearch = !searchTerm ||
      d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.code || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchActive = filterActive === 'all' ||
      (filterActive === 'active' && d.isActive) ||
      (filterActive === 'inactive' && !d.isActive);
    return matchSearch && matchActive;
  });

  const getStatusBadge = (d: Discount) => {
    if (!d.isActive) return <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">Inactivo</span>;
    if (d.isExpired) return <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-600">Vencido</span>;
    if (d.isNotYetValid) return <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">Aún no vigente</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">Activo</span>;
  };

  const getValueDisplay = (d: Discount) => {
    if (d.type === 'PERCENTAGE') {
      const pct = parseFloat(d.value).toFixed(0);
      const max = d.maxAmount ? ` (máx $${parseFloat(d.maxAmount).toLocaleString('es-MX', { minimumFractionDigits: 2 })})` : '';
      return `${pct}%${max}`;
    }
    return `$${parseFloat(d.value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const activeProducts = products.filter(p => p.isActive);
  const filteredProductOptions = activeProducts.filter(p =>
    !productSearch ||
    p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.sku.toLowerCase().includes(productSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Tag className="w-7 h-7 text-emerald-600" />
              Catálogo de Descuentos
            </h1>
            <p className="text-sm text-gray-500 mt-1">Descuentos vinculados a productos para aplicación automática en facturas</p>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Nuevo Descuento
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 mb-4 flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            placeholder="Buscar por nombre o código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <div className="flex gap-2">
            {(['all', 'active', 'inactive'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilterActive(f)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterActive === f
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f === 'all' ? 'Todos' : f === 'active' ? 'Activos' : 'Inactivos'}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-lg border p-12 text-center text-gray-400">Cargando descuentos...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center">
            <Tag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No hay descuentos{searchTerm ? ' que coincidan con la búsqueda' : ''}</p>
            {!searchTerm && (
              <p className="text-gray-400 text-sm mt-1">Crea tu primer descuento y vincúlalo a un producto para que se aplique automáticamente</p>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-medium">Nombre / Código</th>
                  <th className="text-left py-3 px-4 font-medium">Tipo</th>
                  <th className="text-right py-3 px-4 font-medium">Valor</th>
                  <th className="text-left py-3 px-4 font-medium">Productos vinculados</th>
                  <th className="text-center py-3 px-4 font-medium">Vigencia</th>
                  <th className="text-center py-3 px-4 font-medium">Estado</th>
                  <th className="text-center py-3 px-4 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(d => {
                  const linked = getLinkedProducts(d.id);
                  return (
                    <tr key={d.id} className="border-t hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">{d.name}</div>
                        {d.code && <div className="text-xs text-gray-400 font-mono">{d.code}</div>}
                        {d.description && <div className="text-xs text-gray-500 mt-0.5">{d.description}</div>}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          d.type === 'PERCENTAGE' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                        }`}>
                          {d.type === 'PERCENTAGE' ? 'Porcentaje' : 'Monto Fijo'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-semibold text-emerald-600">
                        {getValueDisplay(d)}
                      </td>
                      <td className="py-3 px-4">
                        {linked.length === 0 ? (
                          <span className="text-xs text-gray-300 italic">Sin vincular</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {linked.map(p => (
                              <span key={p.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <ShoppingCart className="w-3 h-3" />
                                <span className="font-mono text-[10px] text-emerald-500">{p.sku}</span>
                                {p.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center text-xs text-gray-500">
                        {d.validFrom || d.validTo ? (
                          <div>
                            {d.validFrom && <div>Desde: {d.validFrom}</div>}
                            {d.validTo && <div>Hasta: {d.validTo}</div>}
                          </div>
                        ) : <span className="text-gray-300">Sin límite</span>}
                      </td>
                      <td className="py-3 px-4 text-center">{getStatusBadge(d)}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleToggleActive(d)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            title={d.isActive ? 'Desactivar' : 'Activar'}
                          >
                            {d.isActive
                              ? <ToggleRight className="w-4 h-4 text-emerald-500" />
                              : <ToggleLeft className="w-4 h-4 text-gray-400" />
                            }
                          </button>
                          <button
                            onClick={() => openEdit(d)}
                            className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4 text-blue-500" />
                          </button>
                          <button
                            onClick={() => handleDelete(d.id)}
                            className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-bold text-gray-900">
                {editing ? 'Editar Descuento' : 'Nuevo Descuento'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Ej. Descuento por pronto pago"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código (Opcional)</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Ej. PRONTOPAGO10"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData(p => ({ ...p, type: e.target.value as any }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="PERCENTAGE">Porcentaje (%)</option>
                    <option value="FIXED">Monto Fijo ($)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor * {formData.type === 'PERCENTAGE' ? '(%)' : '($)'}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={formData.type === 'PERCENTAGE' ? '100' : undefined}
                    step="0.01"
                    value={formData.value}
                    onChange={(e) => setFormData(p => ({ ...p, value: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder={formData.type === 'PERCENTAGE' ? '10' : '500.00'}
                  />
                </div>
              </div>

              {formData.type === 'PERCENTAGE' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monto Máximo de Descuento (Opcional, $)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.maxAmount}
                    onChange={(e) => setFormData(p => ({ ...p, maxAmount: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Sin límite"
                  />
                  <p className="text-xs text-gray-400 mt-1">El descuento no excederá este monto aunque el porcentaje sea mayor</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Válido desde</label>
                  <input
                    type="date"
                    value={formData.validFrom}
                    onChange={(e) => setFormData(p => ({ ...p, validFrom: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Válido hasta</label>
                  <input
                    type="date"
                    value={formData.validTo}
                    onChange={(e) => setFormData(p => ({ ...p, validTo: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción (Opcional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  rows={2}
                  placeholder="Condiciones o notas sobre este descuento..."
                />
              </div>

              {/* Product linking section */}
              <div className="border border-emerald-200 rounded-lg bg-emerald-50/40 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-emerald-600" />
                  <label className="text-sm font-medium text-gray-700">
                    Productos vinculados
                    <span className="text-gray-400 font-normal ml-1 text-xs">(se aplican automáticamente en nuevas facturas)</span>
                  </label>
                </div>

                {/* Selected products tags */}
                {linkedProductIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {linkedProductIds.map(pid => {
                      const p = products.find(x => x.id === pid);
                      if (!p) return null;
                      return (
                        <span key={pid} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-800 border border-emerald-300">
                          <span className="font-mono text-[10px] text-emerald-500">{p.sku}</span>
                          {p.name}
                          <button
                            type="button"
                            onClick={() => removeLinkedProduct(pid)}
                            className="ml-0.5 text-emerald-500 hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Searchable product selector */}
                <div className="relative" ref={productDropdownRef}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                      onFocus={() => setShowProductDropdown(true)}
                      placeholder="Buscar y seleccionar productos..."
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                    />
                  </div>

                  {showProductDropdown && filteredProductOptions.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredProductOptions.map(p => {
                        const isSelected = linkedProductIds.includes(p.id);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => { toggleProduct(p.id); setProductSearch(''); }}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-emerald-50 transition-colors ${isSelected ? 'bg-emerald-50' : ''}`}
                          >
                            <span className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center text-[10px] font-bold ${
                              isSelected ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-gray-300'
                            }`}>
                              {isSelected && '✓'}
                            </span>
                            <span className="font-mono text-xs text-gray-400">{p.sku}</span>
                            <span className="flex-1 truncate">{p.name}</span>
                            {p.defaultDiscountId && p.defaultDiscountId !== (editing?.id) && (
                              <span className="text-xs text-amber-600 bg-amber-50 px-1 rounded">otro dscto.</span>
                            )}
                          </button>
                        );
                      })}
                      {filteredProductOptions.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-400">Sin resultados</div>
                      )}
                    </div>
                  )}
                </div>

                {activeProducts.length === 0 && (
                  <p className="text-xs text-gray-400">No hay productos activos en el catálogo.</p>
                )}
              </div>

              <div className="flex items-center gap-3">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(p => ({ ...p, isActive: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Activo</label>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Crear Descuento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
